import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { get } from 'aws-amplify/api';
import { usePreferences } from '../contexts/PreferencesContext';

interface DayCalories {
  date: string;
  calories: number;
  dayName: string;
  dayShort: string;
  isToday: boolean;
}

const WeeklyDays: React.FC = () => {
  const { preferences } = usePreferences();
  const [weekData, setWeekData] = useState<DayCalories[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchWeeklyData = async () => {
      try {
        setLoading(true);
        const session = await fetchAuthSession();
        const authToken = session.tokens?.idToken?.toString();

        if (!authToken) {
          throw new Error('Not authenticated');
        }

        // Generate dates for the current week (Monday to Sunday)
        const today = new Date();
        const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay; // Adjust for Monday start
        
        const weekDates: DayCalories[] = [];
        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const dayShorts = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

        for (let i = 0; i < 7; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + mondayOffset + i);
          const dateStr = date.toISOString().split('T')[0];
          const isToday = dateStr === today.toISOString().split('T')[0];

          weekDates.push({
            date: dateStr,
            calories: 0,
            dayName: dayNames[i],
            dayShort: dayShorts[i],
            isToday
          });
        }

        // Fetch calories for each day
        const promises = weekDates.map(async (day) => {
          try {
            const restOperation = get({
              apiName: 'FoodTrackerAPI',
              path: '/food/meals',
              options: {
                queryParams: {
                  date: day.date
                },
                headers: {
                  Authorization: authToken
                }
              }
            });

            const { body } = await restOperation.response;
            const response = await body.json() as { meals: any[] };
            
            if (response && response.meals) {
              const totalCalories = response.meals.reduce((sum: number, meal: any) => sum + (meal.totalCalories || 0), 0);
              return { ...day, calories: totalCalories };
            }
            return day;
          } catch (error) {
            console.error(`Error fetching data for ${day.date}:`, error);
            return day;
          }
        });

        const results = await Promise.all(promises);
        setWeekData(results);
      } catch (error) {
        console.error('Error fetching weekly data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyData();
  }, []);

  if (loading) {
    return (
      <div className="weekly-days-inline">
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={index} className="day-circle-inline loading-circle">
            <div className="day-label">-</div>
            <div className="day-calories">-</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="weekly-days-inline">
      {weekData.map((day) => {
        const isOverTarget = day.calories > preferences.dailyCalorieTarget;
        const hasData = day.calories > 0;
        
        return (
          <Link
            key={day.date}
            to={`/history?date=${day.date}`}
            className={`day-circle-inline ${isOverTarget ? 'over-target' : hasData ? 'under-target' : 'no-data'} ${day.isToday ? 'today' : ''}`}
            title={`${day.dayName}: ${hasData ? `${day.calories} calories` : 'No data'}`}
          >
            <div className="day-label">{day.dayShort}</div>
            <div className="day-calories">{hasData ? day.calories : '•'}</div>
          </Link>
        );
      })}
    </div>
  );
};

export default WeeklyDays;